// 1. React
import { useState } from 'react';

// 2. External libraries
import { useUser } from '@clerk/clerk-react';

// 3. Internal components
import { CheckboxWithLink } from '~/components/common/CheckboxWithLink';
import { ErrorAlert } from '~/components/common/ErrorAlert';
import { Button } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';

// 5. Custom hooks and utilities
import { useAsyncForm } from '~/hooks/useAsyncForm';
import { useUserMetadataMutation } from '~/hooks/useUserMetadataMutation';

export interface TermsAcceptanceFormProps {
  /** User email address to display in welcome message */
  userEmail: string;
  /** Callback invoked after successful terms acceptance */
  onAccept: () => void;
  /** Callback invoked when user signs out */
  onSignOut: () => void;
}

/**
 * Terms of Service and Privacy Policy acceptance form.
 *
 * Requires explicit consent via checkboxes before account creation.
 * Updates Clerk publicMetadata with termsAccepted flag and timestamp.
 *
 * Per §TS.3.1: Function declaration for named export
 * Per §TS.3.2: Props interface with descriptive naming
 */
export function TermsAcceptanceForm({ userEmail, onAccept, onSignOut }: TermsAcceptanceFormProps) {
  const { user } = useUser();
  const metadataMutation = useUserMetadataMutation();
  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [privacyChecked, setPrivacyChecked] = useState<boolean>(false);

  const isFormValid = termsChecked && privacyChecked;

  const {
    submit,
    isSubmitting: isLoading,
    error,
  } = useAsyncForm(
    async () => {
      if (!user) {
        throw new Error('User not loaded');
      }
      await metadataMutation.mutateAsync({
        termsAccepted: true,
        termsAcceptedAt: new Date().toISOString(),
      });
      // Reload user to sync publicMetadata before navigation
      await user.reload();
    },
    {
      onSuccess: onAccept,
      mapError: (err) => (err instanceof Error ? err.message : 'Failed to accept terms'),
    }
  );

  const handleSubmit = async () => {
    if (!user || !isFormValid) {
      return;
    }
    await submit();
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Welcome Message */}
      <div className="flex flex-col gap-4">
        <p className="text-base">You are about to create a new account as {userEmail}!</p>
        <p className="text-base">
          Please review and accept our terms of service and privacy policy to continue creating an
          account.
        </p>
      </div>

      {/* Error Alert */}
      {error && <ErrorAlert message={error} />}

      {/* Terms Checkboxes */}
      <div className="flex flex-col gap-2">
        <CheckboxWithLink
          checked={termsChecked}
          onChange={setTermsChecked}
          label="I agree to the"
          linkText="Terms of Service"
          href="https://deaddrop.sh/terms/"
          disabled={isLoading}
        />
        <CheckboxWithLink
          checked={privacyChecked}
          onChange={setPrivacyChecked}
          label="I agree to the"
          linkText="Privacy Policy"
          href="https://deaddrop.sh/privacy/"
          disabled={isLoading}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-4">
        <Button
          onClick={() => void handleSubmit()}
          disabled={isLoading || !isFormValid}
          className="w-full"
          aria-busy={isLoading}
        >
          {isLoading && (
            <Spinner
              data-icon="inline-start"
              data-testid="terms-submit-spinner"
              aria-hidden="true"
            />
          )}
          Accept and continue
        </Button>
        <Button
          variant="link"
          onClick={onSignOut}
          disabled={isLoading}
          className="text-sm underline"
        >
          Cancel and Sign Out
        </Button>
      </div>
    </div>
  );
}
